import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

import { OutroPropsExtented } from "@/common/Outro";
import PostRegistrationPromo from "@/common/Outro/RegistrationForm/promo/PostRegistrationPromo";
import LightRegistrationForm from "@/common/Outro/RegistrationForm/templates/light/LightRegistrationForm";
import {
  FormButton,
  FormCheckbox,
  FormGroup,
  FormInput,
  FormStatic,
} from "@/components/Form";
import FormSelect from "@/components/Form/Select";
import { getApiUrl, getDocumentUrl, PRIVACY_STATEMENT_LINK_AS } from "@/config";
import { GlobalContext } from "@/contexts";
import { useTimeout } from "@/hooks";
import LocaleContext from "@/i18n";
import EventSystem from "@/service/EventSystem";
import IFrameService, { ParticipationTokenResponse } from "@/service/IFrame";
import InteractionService from "@/service/Interaction";
import {
  AutofillContact,
  CustomField,
  CustomFieldType,
  NewContactInput,
  Participant,
  ParticipantAutofill,
  ParticipationSubmit,
  RegistrationFormInteractionOutro,
  RegistrationStatus,
} from "@/service/Model";
import Svg from "@/styles/utils/Svg";
import { log } from "@/utils/logger";

import { OutroFeedback, OutroFeedbackIcon } from "../styles";
import ClassicRegistrationForm from "./templates/classic/ClassicRegistrationForm";

const TIMEOUT_TO_RECEIVE_POST_MESSAGE_WITH_HANS_ID = 200;

export interface RegistrationFormProps extends OutroPropsExtented {
  registrationStatus?: RegistrationStatus;
  hansId?: string;
  hansIdExists?: boolean;
  onSubmit?: (payload: Record<string, string | boolean> | Participant) => void;
  onSubmitEnd?: (status: RegistrationStatus) => void;
  outro: RegistrationFormInteractionOutro;
  lightLayout: boolean;
  initialParticipant?: Partial<Participant>;
  initialFormState?: RegistrationFormState;
  onFormStateChange?: (formState: RegistrationFormState) => void;
}

export type RegistrationFormState = Record<string, string | boolean>;

type AutofillState = {
  token: string;
  profile: ParticipantAutofill;
};

const MANUAL_ADDRESS = "manual";
const PENDING_AUTOFILL_KEY_PREFIX = "iat.autofill.pending.";
// Entered contact data (name/email/address/phone) is parked only to survive the login round-trip.
// It lives in sessionStorage — per-tab, cleared on tab/browser close and invisible to other tabs or
// browser users — and the TTL bounds it further so an abandoned same-tab login never leaves stale PII.
const PENDING_AUTOFILL_TTL_MS = 30 * 60 * 1000;
const SUBJECT_LABEL = "Anmerkungen";

export const RegistrationForm: React.FC<RegistrationFormProps> = (
  props: RegistrationFormProps
): React.JSX.Element => {
  const {
    onSubmitEnd,
    lightLayout,
    outro: { hansIdOptIn },
  } = props;
  // Autofill only applies to full-address forms. Light variants (lightLayout / lightRegistration)
  // collect name+email only, so a NewContact can never be built — the consent, v2 submit and login
  // redirect must not engage there; those forms fall through to the legacy path unchanged.
  const autofillApplies =
    props.outro.autofillEnabled === true &&
    !lightLayout &&
    props.outro.lightRegistration !== true;
  const globalContext = useContext(GlobalContext);
  const interactionID = globalContext.response?.interaction.id;
  const [pendingFormState] = useState<RegistrationFormState | undefined>(() =>
    autofillApplies && interactionID
      ? restorePendingFormState(interactionID)
      : undefined
  );
  const [hansIdExists, setHansIdExists] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [autofill, setAutofill] = useState<AutofillState>();
  const [selectedContactId, setSelectedContactId] = useState<
    string | undefined
  >();
  const [useManualAddress, setUseManualAddress] = useState<boolean>(false);
  // Consent to keep a newly entered address in the profile beyond this Gewinnspiel. Without it the
  // entry still goes through, but on the legacy path, so nothing is written to the user's profile.
  const [saveContactConsent, setSaveContactConsent] = useState<boolean>(false);
  const [autofillAdvertisementOptIn, setAutofillAdvertisementOptIn] =
    useState<boolean>(false);
  const [autofillSubject, setAutofillSubject] = useState<string>("");
  const [autofillCustomFields, setAutofillCustomFields] = useState<string[]>(
    []
  );
  const [autofillRegistrationStatus, setAutofillRegistrationStatus] =
    useState<RegistrationStatus>();
  const formStateRef = useRef<RegistrationFormState>({
    ...toRegistrationFormState(props.initialParticipant),
    ...pendingFormState,
  });

  const localeContext = useContext(LocaleContext);
  const { translation } = localeContext;
  const { registrationStatus, preview, hansId, handleRegistration, icons } =
    globalContext;
  // hansId reference is needed for timeout callback
  const hansIdRef = useRef<string | undefined>(hansId);

  const api = useMemo(
    () => new InteractionService(getApiUrl(preview)),
    [preview]
  );

  useEffect(() => {
    // Get hansId from adLib - useEffect on 'hansId' will be triggered
    IFrameService.getHansId();
    if (!autofillApplies) {
      return;
    }

    let active = true;
    const unsubscribe = EventSystem.subscribe(
      "participationToken:received",
      (data: ParticipationTokenResponse | undefined) => {
        if (data?.status === "unauthenticated") {
          if (interactionID) {
            savePendingFormState(interactionID, formStateRef.current);
          }
          IFrameService.redirectToLogin();
          return;
        }
        if (data?.status !== "authenticated" || !data.participationToken) {
          return;
        }
        const participationToken = data.participationToken;
        api
          .getParticipantAutofill(participationToken)
          .then((response) => {
            if (!active || !response.ok || !response.data) {
              return;
            }
            setAutofill({
              token: participationToken,
              profile: response.data,
            });
            setSelectedContactId(response.data.contacts[0]?.id);
          })
          .catch(() => undefined);
      }
    );
    IFrameService.getParticipationToken();
    return () => {
      active = false;
      unsubscribe();
    };
  }, [api, interactionID, autofillApplies]);

  useEffect(() => {
    hansIdRef.current = hansId;
    if (!hansId || !hansIdOptIn) {
      // hansId is not received or checkbox setting is not configured - no query from the database necessary
      setLoading(false);
      return;
    }
    verifyConsentForHansId(hansId);
  }, [hansId]);

  useTimeout(() => {
    // - Checkbox setting is configured
    // - No hansId received
    // - Not preview mode
    if (hansIdOptIn && !hansIdRef.current && !preview) {
      // Possible reasons:
      // - The use of the Hans-ID can be rejected by the user via a cookie banner, etc.
      // - Timing problem: PostMessage sender and recipient miss each other
      log.info(
        "Registration form without hans-opt-in. Reason: No hans ID received. Referrer: " +
          getDocumentUrl()
      );
    }
  }, TIMEOUT_TO_RECEIVE_POST_MESSAGE_WITH_HANS_ID);

  const verifyConsentForHansId = (hansId: string) => {
    api
      .hansIdExists(hansId)
      .then((exists: boolean) => {
        setHansIdExists(exists);
      })
      .catch(() => {
        setHansIdExists(false);
      })
      .then(() => {
        setLoading(false);
      });
  };

  const handleSubmit = (
    payload: Record<string, string | boolean> | Participant
  ) => {
    const autofillPayload = buildAutofillPayload(payload);
    if (autofillPayload) {
      return submitAutofillParticipation(autofillPayload).then(
        (status: RegistrationStatus) => onSubmitEnd?.(status)
      );
    }

    return handleRegistration(payload).then((status: RegistrationStatus) =>
      onSubmitEnd?.(status)
    );
  };

  const buildBaseAutofillPayload = (
    payload: Partial<ParticipationSubmit>
  ): ParticipationSubmit | undefined => {
    const interaction = globalContext.response?.interaction;
    if (!autofill || !interaction) {
      return undefined;
    }
    return {
      interactionID: interaction.id,
      interactionType: interaction.type,
      campaignId: interaction.outro?.campaignId,
      ...payload,
    };
  };

  const buildAutofillPayload = (
    payload: Record<string, string | boolean> | Participant
  ): ParticipationSubmit | undefined => {
    // No autofill session, or no consent to store the address in the profile, means the v2 path is
    // not available: fall through to the legacy submit so the entry is never blocked.
    if (!autofill || !saveContactConsent) {
      return undefined;
    }

    const data = payload as any;
    const newContact = buildNewContact(data);
    if (!newContact) {
      return undefined;
    }

    return buildBaseAutofillPayload({
      advertisementOptIn: data.advertisementOptIn === true,
      subject: typeof data.subject === "string" ? data.subject : undefined,
      customFields: Array.isArray(data.customFields)
        ? data.customFields
        : undefined,
      newContact,
    });
  };

  const buildNewContact = (payload: any): NewContactInput | undefined => {
    const firstName = String(payload.firstName ?? "");
    const lastName = String(payload.lastName ?? "");
    const email = String(payload.email ?? "");
    const street = String(payload.street ?? "");
    const houseNumber = String(payload.houseNumber ?? "");
    const postalCode = String(payload.postalcode ?? payload.postalCode ?? "");
    const city = String(payload.city ?? "");

    if (
      !firstName ||
      !lastName ||
      !email ||
      !street ||
      !houseNumber ||
      !postalCode ||
      !city
    ) {
      return undefined;
    }

    return {
      salutation: String(payload.gender ?? ""),
      gender: String(payload.gender ?? ""),
      firstName,
      lastName,
      email,
      telephone: String(payload.telephone ?? ""),
      street,
      houseNumber,
      postalCode,
      city,
    };
  };

  const submitAutofillParticipation = async (
    payload: ParticipationSubmit
  ): Promise<RegistrationStatus> => {
    if (!autofill) {
      return RegistrationStatus.Error;
    }

    setAutofillRegistrationStatus(RegistrationStatus.Pending);
    try {
      const response = await api.postParticipationV2(payload, autofill.token);
      if (response.ok || response.status === 202) {
        setAutofillRegistrationStatus(RegistrationStatus.Success);
        return RegistrationStatus.Success;
      }
      setAutofillRegistrationStatus(RegistrationStatus.Error);
      log.error(
        `Error while submit autofill registration form: ${response.status}: ${response.text}`
      );
      return RegistrationStatus.Error;
    } catch (e) {
      setAutofillRegistrationStatus(RegistrationStatus.Error);
      log.error("Error while submit autofill registration form:" + e);
      return RegistrationStatus.Error;
    }
  };

  const handleSelectedContactSubmit = (): void => {
    if (!selectedContactId || !validateAutofillChooser()) {
      return;
    }

    const payload = buildBaseAutofillPayload({
      advertisementOptIn: autofillAdvertisementOptIn,
      subject: props.outro.registrationCustomOption
        ? autofillSubject
        : undefined,
      customFields: props.outro.customFields?.length
        ? props.outro.customFields.map(
            (_customField: CustomField, index: number) =>
              autofillCustomFields[index] ?? ""
          )
        : undefined,
      selectedContactId,
    });
    if (!payload) {
      return;
    }

    submitAutofillParticipation(payload).then((status: RegistrationStatus) =>
      onSubmitEnd?.(status)
    );
  };

  const handleAutofillCustomFieldChange = (
    index: number,
    value: string
  ): void => {
    setAutofillCustomFields((currentCustomFields) => {
      const nextCustomFields = [...currentCustomFields];
      nextCustomFields[index] = value;
      return nextCustomFields;
    });
  };

  const validateAutofillChooser = (): boolean => {
    if (props.outro.registrationCustomOption && autofillSubject.length === 0) {
      return false;
    }

    return (props.outro.customFields ?? []).every(
      (_customField: CustomField, index: number) =>
        (autofillCustomFields[index] ?? "").length > 0
    );
  };

  const formatContact = (contact: AutofillContact): string =>
    [
      [contact.firstName, contact.lastName].filter(Boolean).join(" "),
      [contact.street, contact.houseNumber].filter(Boolean).join(" "),
      [contact.postalCode, contact.city].filter(Boolean).join(" "),
    ]
      .filter(Boolean)
      .join(", ");

  const getInitialParticipant = (): Partial<Participant> | undefined => {
    if (!autofill?.profile.identity) {
      return props.initialParticipant;
    }

    return {
      ...props.initialParticipant,
      firstName: autofill.profile.identity.firstName,
      lastName: autofill.profile.identity.lastName,
      email: autofill.profile.identity.email,
      gender: autofill.profile.identity.gender,
      telephone: autofill.profile.identity.telephone,
    };
  };

  const handleFormStateChange = (formState: RegistrationFormState): void => {
    formStateRef.current = formState;
  };

  const renderAutofillStatus = (
    status: RegistrationStatus
  ): React.JSX.Element => {
    let icon = null;
    let feedback = translation.request.pending;

    if (status === RegistrationStatus.Error) {
      icon = icons.Fail;
      feedback = translation.request.error;
    } else if (status === RegistrationStatus.Success) {
      icon = icons.Success;
      feedback = translation.thankYou;
    }

    return (
      <>
        <OutroFeedback data-qa="registration.outro.feedback">
          {icon && (
            <OutroFeedbackIcon $success={status === RegistrationStatus.Success}>
              <Svg contents={icon} height={65} width={65} />
            </OutroFeedbackIcon>
          )}
          <h3>{feedback}</h3>
        </OutroFeedback>
        {status === RegistrationStatus.Success &&
          props.outro.promo?.enabled && (
            <PostRegistrationPromo outro={props.outro} />
          )}
      </>
    );
  };

  const renderAutofillChooser = (): React.JSX.Element => {
    const currentStatus = autofillRegistrationStatus ?? registrationStatus;
    if (currentStatus && currentStatus !== RegistrationStatus.None) {
      return renderAutofillStatus(currentStatus);
    }

    const contactOptions =
      autofill?.profile.contacts.map((contact) => ({
        label: formatContact(contact),
        value: contact.id,
      })) ?? [];
    const submitDisabled = !selectedContactId || !validateAutofillChooser();

    return (
      <div data-qa="registration.autofill.chooser">
        <FormStatic>
          <b>{translation.registration.autofillChooserTitle}</b>
        </FormStatic>
        <FormStatic>
          <div
            data-qa="registration.autofill.existingAddressNotice"
            style={autofillStyles.legalNotice}
          >
            {translation.registration.autofillExistingAddressNotice}
          </div>
        </FormStatic>
        <div style={autofillStyles.options}>
          {contactOptions.map((option) => (
            <label key={option.value} style={autofillStyles.option}>
              <input
                data-qa={`registration.autofill.contact.${option.value}`}
                type="radio"
                name="autofillContact"
                value={option.value}
                checked={selectedContactId === option.value}
                onChange={() => setSelectedContactId(option.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
          <label style={autofillStyles.option}>
            <input
              data-qa="registration.autofill.manual"
              type="radio"
              name="autofillContact"
              value={MANUAL_ADDRESS}
              checked={useManualAddress}
              onChange={() => setUseManualAddress(true)}
            />
            <span>{translation.registration.autofillOtherAddress}</span>
          </label>
        </div>
        {props.outro.registrationCustomOption && (
          <FormGroup>
            <FormInput
              name="autofillSubject"
              label={SUBJECT_LABEL}
              required={{
                active: true,
                text: translation.registration.required,
              }}
              value={autofillSubject}
              onChange={setAutofillSubject}
              valid={true}
            />
          </FormGroup>
        )}
        {(props.outro.customFields ?? []).map(
          (customField: CustomField, index: number) => {
            const name = `autofillCustomField-${index}`;
            const value = autofillCustomFields[index] ?? "";
            return (
              <FormGroup key={name}>
                {customField.type === CustomFieldType.Select ? (
                  <FormSelect
                    name={name}
                    label={customField.label}
                    required={{
                      active: true,
                      text: translation.registration.required,
                    }}
                    value={value}
                    options={customField.options ?? []}
                    onChange={(nextValue: string) =>
                      handleAutofillCustomFieldChange(index, nextValue)
                    }
                    valid={true}
                  />
                ) : (
                  <FormInput
                    name={name}
                    label={customField.label}
                    required={{
                      active: true,
                      text: translation.registration.required,
                    }}
                    value={value}
                    onChange={(nextValue: string) =>
                      handleAutofillCustomFieldChange(index, nextValue)
                    }
                    valid={true}
                  />
                )}
              </FormGroup>
            );
          }
        )}
        {props.outro.advertisementOptIn && (
          <div style={autofillStyles.checkbox}>
            <FormCheckbox
              icons={icons}
              name="autofillAdvertisementOptIn"
              label={translation.registration.advertisementOptInLabelAS(
                PRIVACY_STATEMENT_LINK_AS
              )}
              onChange={setAutofillAdvertisementOptIn}
              checked={autofillAdvertisementOptIn}
            />
          </div>
        )}
        <FormStatic>
          <div
            dangerouslySetInnerHTML={{
              __html: translation.registration.termsAndConditions(
                props.outro.termsAndConditionsLink
              ),
            }}
          />
          <div>
            <a
              href={props.outro.privacyPolicyLink}
              target="_blank"
              rel="noreferrer"
            >
              {translation.registration.privacy}
            </a>
          </div>
        </FormStatic>
        <FormButton
          type="submit"
          disabled={submitDisabled}
          label={translation.registration.submit}
          onClick={handleSelectedContactSubmit}
          dataQA="registration.autofill.submit"
        />
      </div>
    );
  };

  const renderRegistrationForm = (): React.JSX.Element =>
    lightLayout ? (
      <LightRegistrationForm
        {...props}
        initialParticipant={getInitialParticipant()}
        initialFormState={pendingFormState}
        onFormStateChange={
          autofillApplies ? handleFormStateChange : undefined
        }
        hansId={hansId}
        hansIdExists={hansIdExists}
        registrationStatus={autofillRegistrationStatus ?? registrationStatus}
        onSubmit={handleSubmit}
      />
    ) : (
      <ClassicRegistrationForm
        {...props}
        initialParticipant={getInitialParticipant()}
        initialFormState={pendingFormState}
        onFormStateChange={
          autofillApplies ? handleFormStateChange : undefined
        }
        hansId={hansId}
        hansIdExists={hansIdExists}
        registrationStatus={autofillRegistrationStatus ?? registrationStatus}
        onSubmit={handleSubmit}
      />
    );

  if (loading) {
    return <></>;
  }

  return (
    <div data-hans-id-received={!!hansId}>
      {autofill?.profile.contacts.length && !useManualAddress ? (
        renderAutofillChooser()
      ) : (
        <>
          {autofill && (
            <div style={autofillStyles.checkbox}>
              <FormCheckbox
                icons={icons}
                name="saveContactConsent"
                label={translation.registration.autofillSaveConsentLabel}
                onChange={setSaveContactConsent}
                checked={saveContactConsent}
              />
            </div>
          )}
          {renderRegistrationForm()}
        </>
      )}
    </div>
  );
};

const getPendingAutofillKey = (interactionID: string): string =>
  `${PENDING_AUTOFILL_KEY_PREFIX}${interactionID}`;

const restorePendingFormState = (
  interactionID: string
): RegistrationFormState | undefined => {
  const key = getPendingAutofillKey(interactionID);
  let serialized: string | null;
  try {
    serialized = window.sessionStorage.getItem(key);
  } catch {
    return undefined;
  }
  if (!serialized) {
    return undefined;
  }
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // The form remains usable even when storage is unavailable.
  }
  try {
    const parsed = JSON.parse(serialized);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return undefined;
    }
    const savedAt =
      typeof parsed.savedAt === "number" ? parsed.savedAt : 0;
    if (!savedAt || Date.now() - savedAt > PENDING_AUTOFILL_TTL_MS) {
      // Expired (or legacy/no timestamp): already removed above, do not restore stale PII.
      return undefined;
    }
    return toRegistrationFormState(parsed.formState);
  } catch {
    return undefined;
  }
};

const savePendingFormState = (
  interactionID: string,
  formState: RegistrationFormState
): void => {
  try {
    window.sessionStorage.setItem(
      getPendingAutofillKey(interactionID),
      JSON.stringify({ savedAt: Date.now(), formState })
    );
  } catch {
    // The login redirect is still safe when storage is unavailable.
  }
};

const toRegistrationFormState = (value: unknown): RegistrationFormState => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }
  return Object.entries(value).reduce((formState, [fieldName, fieldValue]) => {
    if (typeof fieldValue === "string" || typeof fieldValue === "boolean") {
      formState[fieldName] = fieldValue;
    }
    return formState;
  }, {} as RegistrationFormState);
};

const autofillStyles: {
  checkbox: React.CSSProperties;
  legalNotice: React.CSSProperties;
  option: React.CSSProperties;
  options: React.CSSProperties;
} = {
  checkbox: {
    marginBottom: 18,
  },
  // The legal wording contains a deliberate line break; keep it rather than collapsing the text.
  legalNotice: {
    whiteSpace: "pre-line",
  },
  option: {
    alignItems: "flex-start",
    cursor: "pointer",
    display: "flex",
    fontSize: 16,
    gap: 10,
    lineHeight: 1.35,
    marginBottom: 12,
  },
  options: {
    margin: "16px 0 18px",
  },
};
